import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../components/common/Button';
import CustomSelect from '../components/common/CustomSelect';
import PageHero from '../components/layout/PageHero';
import { getTaskForm, submitTaskApplication, verifyTaskEmail } from '../services/taskApplicationService';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phonePattern = /^[0-9+\-\s()]{7,20}$/;

function getFieldOptions(field) {
  return Array.isArray(field.options) ? field.options.filter((option) => String(option || '').trim()) : [];
}

function getInitialResponses(form) {
  const responses = {};

  (form?.categories || []).forEach((category) => {
    category.fields.forEach((field) => {
      responses[field.key] = field.type === 'checkbox' ? [] : '';
    });
  });

  return responses;
}

function isEmailField(field) {
  return field.type === 'email' || ['email', 'application-email'].includes(field.key);
}

function findEmailKey(fields) {
  return fields.find(isEmailField)?.key || 'email';
}

function validateField(field, value, file) {
  if (field.type === 'file') {
    if (field.required && !file) {
      return `${field.label} is required.`;
    }

    if (!file) {
      return '';
    }

    const maxBytes = (Number(field.maxFileSizeMB) || 10) * 1024 * 1024;
    const extension = file.name.split('.').pop().toLowerCase();
    const allowedTypes = field.allowedFileTypes || [];

    if (file.size > maxBytes) {
      return `${field.label} must be ${field.maxFileSizeMB || 10}MB or smaller.`;
    }

    if (allowedTypes.length && !allowedTypes.includes(extension)) {
      return `${field.label} must be one of: ${allowedTypes.join(', ')}.`;
    }

    return '';
  }

  if (field.required && (Array.isArray(value) ? value.length === 0 : !String(value || '').trim())) {
    return `${field.label} is required.`;
  }

  if (!value) {
    return '';
  }

  if (field.type === 'email' && !emailPattern.test(value)) {
    return `${field.label} must be a valid email address.`;
  }

  if (field.type === 'phone' && !phonePattern.test(value)) {
    return `${field.label} must be a valid phone number.`;
  }

  if (field.type === 'url') {
    try {
      new URL(value);
    } catch (error) {
      return `${field.label} must be a valid URL.`;
    }
  }

  return '';
}

function getEligibilityMessage(result) {
  if (result?.reason === 'not_applied') {
    return (
      <>
        We could not find a job application with this email. Please submit the job application form first before
        completing the task round. <Link to="/apply">Apply first</Link>
      </>
    );
  }

  if (result?.reason === 'rejected') {
    return 'This email is not eligible to submit the task completion form. Please contact FactorStreet HR if you think this is a mistake.';
  }

  return 'This email is not eligible for task submission.';
}

function CompleteTaskPage() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: '', form: null });
  const [responses, setResponses] = useState({});
  const [files, setFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [eligibility, setEligibility] = useState({ checkedEmail: '', checking: false, result: null });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let isMounted = true;

    getTaskForm()
      .then((data) => {
        if (!isMounted) return;
        setState({ loading: false, error: '', form: data.form });
        setResponses(getInitialResponses(data.form));
      })
      .catch(() => {
        if (isMounted) {
          setState({ loading: false, error: 'Unable to load the task completion form right now.', form: null });
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const activeFields = useMemo(
    () => (state.form?.categories || []).flatMap((category) => category.fields || []),
    [state.form]
  );
  const emailKey = useMemo(() => findEmailKey(activeFields), [activeFields]);
  const candidateEmail = String(responses[emailKey] || '').trim().toLowerCase();

  const updateResponse = (field, value) => {
    setResponses((current) => ({ ...current, [field.key]: value }));
    setErrors((current) => ({ ...current, [field.key]: '' }));
    if (isEmailField(field)) {
      setEligibility({ checkedEmail: '', checking: false, result: null });
    }
  };

  const updateCheckbox = (field, option, checked) => {
    const currentValues = responses[field.key] || [];
    const nextValues = checked
      ? [...currentValues, option]
      : currentValues.filter((value) => value !== option);

    updateResponse(field, nextValues);
  };

  const updateFile = (field, file) => {
    setFiles((current) => ({ ...current, [field.key]: file }));
    setErrors((current) => ({ ...current, [field.key]: '' }));
  };

  const validateForm = () => {
    const nextErrors = {};

    activeFields.forEach((field) => {
      const error = validateField(field, responses[field.key], files[field.key]);
      if (error) {
        nextErrors[field.key] = error;
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const runEmailVerification = async () => {
    if (!emailPattern.test(candidateEmail)) {
      setEligibility({ checkedEmail: candidateEmail, checking: false, result: { eligible: false, reason: 'invalid_email' } });
      setErrors((current) => ({ ...current, [emailKey]: 'Enter the same valid email used in your job application.' }));
      return false;
    }

    if (eligibility.checkedEmail === candidateEmail && eligibility.result?.eligible) {
      return true;
    }

    setEligibility({ checkedEmail: candidateEmail, checking: true, result: null });
    const result = await verifyTaskEmail(candidateEmail);
    setEligibility({ checkedEmail: candidateEmail, checking: false, result });

    return Boolean(result.eligible);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFeedback({ type: '', message: '' });

    if (!validateForm()) {
      setFeedback({ type: 'error', message: 'Please fix the highlighted fields before submitting.' });
      return;
    }

    setSubmitting(true);
    try {
      const eligible = await runEmailVerification();
      if (!eligible) {
        setFeedback({ type: 'error', message: 'Please complete email verification before submitting.' });
        setSubmitting(false);
        return;
      }

      await submitTaskApplication({
        email: candidateEmail,
        responses,
        files,
      });
      navigate('/complete-task/thank-you', { replace: true });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error.response?.data?.message || 'Unable to submit your task right now.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const commonProps = {
      id: field.key,
      name: field.key,
      onBlur: isEmailField(field) ? () => runEmailVerification().catch(() => {}) : undefined,
      onChange: (event) => updateResponse(field, event.target.value),
      placeholder: field.placeholder,
      required: field.required,
      value: responses[field.key] || '',
    };

    if (field.type === 'textarea') {
      return <textarea {...commonProps} rows="5" />;
    }

    if (field.type === 'dropdown' || field.type === 'select') {
      const options = getFieldOptions(field);

      return (
        <CustomSelect
          id={field.key}
          name={field.key}
          onChange={(value) => updateResponse(field, value)}
          options={options}
          placeholder="Select an option"
          required={field.required}
          value={responses[field.key] || ''}
        />
      );
    }

    if (field.type === 'radio') {
      const options = getFieldOptions(field);

      return (
        <div className="apply-option-list">
          {options.length === 0 ? <small>This field is not configured yet.</small> : null}
          {options.map((option, index) => (
            <label className="apply-option" key={`${field.key}-${option}-${index}`}>
              <input
                checked={responses[field.key] === option}
                name={field.key}
                onChange={() => updateResponse(field, option)}
                type="radio"
                value={option}
              />
              {option}
            </label>
          ))}
        </div>
      );
    }

    if (field.type === 'checkbox') {
      const options = getFieldOptions(field);

      return (
        <div className="apply-option-list apply-option-list--checkbox">
          {options.length === 0 ? <small>This field is not configured yet.</small> : null}
          {options.map((option, index) => {
            const isChecked = (responses[field.key] || []).includes(option);

            return (
              <label
                className={`apply-option apply-option--checkbox${isChecked ? ' is-selected' : ''}`}
                key={`${field.key}-${option}-${index}`}
              >
                <input
                  checked={isChecked}
                  onChange={(event) => updateCheckbox(field, option, event.target.checked)}
                  type="checkbox"
                  value={option}
                />
                <span>{option}</span>
              </label>
            );
          })}
        </div>
      );
    }

    if (field.type === 'file') {
      const selectedFile = files[field.key];

      return (
        <div className="apply-file-upload">
          <input
            accept={(field.allowedFileTypes || []).map((type) => `.${type}`).join(',')}
            className="sr-only"
            id={field.key}
            name={field.key}
            onChange={(event) => updateFile(field, event.target.files?.[0] || null)}
            required={field.required}
            type="file"
          />
          <label className="apply-file-upload__button" htmlFor={field.key}>
            Choose file
          </label>
          <span className="apply-file-upload__name">{selectedFile?.name || 'No file chosen'}</span>
        </div>
      );
    }

    const inputType = field.type === 'phone' ? 'tel' : field.type;
    return <input {...commonProps} type={inputType} />;
  };

  return (
    <>
      <PageHero
        eyebrow="CAREERS"
        title="Task Completion Form"
        description="This form is for selected candidates invited to the paid task round. Please use the same email address you used while submitting your job application."
      />

      <section className="section apply-page complete-task-page">
        <div className="container apply-layout">
          <aside className="apply-sidebar card">
            <div className="apply-sidebar__copy">
              <span>SELECTED CANDIDATES</span>
            </div>
            <p className="complete-task-note">
              This task round may be incentivised depending on the role and task type. Task submission helps us
              evaluate seriousness, communication, role-fit, and practical skills. Completion does not guarantee final
              selection.
            </p>
            <div className="apply-sidebar__actions">
              <Button to="/hiring-process" variant="secondary">
                Hiring Process
              </Button>
              <Button to="/jobs" variant="secondary">
                Current Openings
              </Button>
            </div>
          </aside>

          <div className="apply-form-shell">
            {state.loading ? <p className="jobs-empty">Loading task completion form...</p> : null}
            {state.error ? <p className="form-alert form-alert--error">{state.error}</p> : null}
            {!state.loading && !state.form ? (
              <div className="jobs-empty-card">
                <h2>Task completion form is not available right now.</h2>
                <p>Please check your HR email or contact FactorStreet HR.</p>
                <Button to="/contact">Contact Us</Button>
              </div>
            ) : null}

            {state.form ? (
              <form className="apply-form" onSubmit={handleSubmit}>
                <div className="apply-form__header">
                  <p className="eyebrow">FACTOR STREET</p>
                  <h2>{state.form.introTitle || state.form.title || 'Task Completion Form'}</h2>
                  {state.form.introDescription ? <p>{state.form.introDescription}</p> : null}
                </div>

                {feedback.message ? (
                  <p className={`form-alert form-alert--${feedback.type === 'error' ? 'error' : 'success'}`}>
                    {feedback.message}
                  </p>
                ) : null}

                {eligibility.checking ? <p className="form-alert">Checking candidate eligibility...</p> : null}
                {eligibility.result?.eligible ? (
                  <p className="form-alert form-alert--success">
                    Verified for {eligibility.result.candidateName || 'candidate'}
                    {eligibility.result.appliedPosition ? ` (${eligibility.result.appliedPosition})` : ''}.
                  </p>
                ) : null}
                {eligibility.result && !eligibility.result.eligible && eligibility.result.reason !== 'invalid_email' ? (
                  <p className="form-alert form-alert--error">{getEligibilityMessage(eligibility.result)}</p>
                ) : null}

                {state.form.categories.map((category) => (
                  <section className="apply-category" key={category.id || category.slug}>
                    <div className="apply-category__header">
                      <h3>{category.title}</h3>
                      {category.description ? <p>{category.description}</p> : null}
                    </div>
                    <div className="apply-field-grid">
                      {category.fields.map((field) => (
                        <div
                          className={`apply-field${
                            field.type === 'checkbox' ||
                            field.type === 'textarea' ||
                            field.type === 'file' ||
                            field.type === 'url'
                              ? ' apply-field--full'
                              : ''
                          }`}
                          key={field.id || field.key}
                        >
                          <label htmlFor={field.key}>
                            {field.label}
                            {field.required ? <strong> *</strong> : null}
                          </label>
                          {renderField(field)}
                          {field.helpText ? <small>{field.helpText}</small> : null}
                          {errors[field.key] ? <small className="apply-field__error">{errors[field.key]}</small> : null}
                        </div>
                      ))}
                    </div>
                  </section>
                ))}

                <button className="btn btn--primary apply-submit" disabled={submitting || eligibility.checking} type="submit">
                  {submitting ? 'Submitting...' : 'Submit Task'}
                </button>
              </form>
            ) : null}
          </div>
        </div>
      </section>
    </>
  );
}

export default CompleteTaskPage;
